import { Link } from "react-router-dom";
import { ChefHat, Building2, Home, ArrowRight, FileText, ShieldCheck, Clock, MapPin } from "lucide-react";

const OPTIONS = [
  {
    href: "/mietkoch-werden",
    icon: ChefHat,
    eyebrow: "Für selbstständige Köche",
    title: "Als Mietkoch oder Privatkoch registrieren",
    description:
      "Du bist selbstständiger Koch und willst Einsätze, ohne dich um Akquise zu kümmern? Ein Profil, zwei Möglichkeiten: Einsätze in Betrieben und — wenn du magst — Private Dining bei Privatkunden.",
    bullets: [
      "Ein Profil für beides: Betriebe und Privatkunden",
      "Du kreuzt selbst an, was du machen willst",
      "Für dich dauerhaft kostenlos — die Provision zahlt der Auftraggeber",
      "Du entscheidest frei über jeden Einsatz, keine Annahmepflicht",
      "Keine Bindung, kein Kundenschutz, jederzeit kündbar",
      "Einsätze in Deutschland und Österreich — du bestimmst den Umkreis",
    ],
    cta: "Profil anlegen",
    testid: "vermittlung-choice-koch",
  },
  {
    href: "/vermittlung/koch-anfordern",
    icon: Building2,
    eyebrow: "Für Gastronomie & Hotellerie",
    title: "Mietkoch anfordern",
    description:
      "Personalausfall, Saisonspitze oder eine offene Stelle? Ich vermittle Ihnen einen geprüften selbstständigen Koch aus meinem Netzwerk — schnell und unkompliziert.",
    bullets: [
      "40 € netto pro Koch und Einsatztag, sonst nichts",
      "Alle Unterlagen vorab geprüft: Gewerbe, Lebenslauf, Hygienenachweis",
      "Rückmeldung innerhalb von 24 Stunden",
      "Keine Grundgebühr, kein Kundenschutz, keine Bindung",
      "Betriebe in Deutschland und Österreich",
      "Festanstellung vermitteln: 100 € netto einmalig",
    ],
    cta: "Anfrage stellen",
    testid: "vermittlung-choice-betrieb",
  },
  {
    icon: Home,
    href: "/vermittlung/privatkoch-anfragen",
    eyebrow: "Für Privatkunden & Familien",
    title: "Privatkoch anfragen",
    description: "Geburtstag, Hochzeit oder Dinner mit Freunden: Ich vermittle Ihnen einen selbstständigen Koch, der bei Ihnen zu Hause kocht.",
    bullets: [
      "Passender Koch aus meinem Netzwerk",
      "Honorar und Einkauf direkt mit dem Koch",
      "Provision 47,60 € inkl. MwSt. je Koch und Einsatztag",
      "Kein Abo, keine Grundgebühr, keine Bindung",
    ],
    cta: "Anfrage stellen",
    testid: "vermittlung-choice-privat",
  },
];

const TRUST = [
  { icon: ShieldCheck, title: "Geprüfte Kollegen", text: "Jeder Koch legt Gewerbenachweis, Lebenslauf und Hygienenachweis vor, bevor ich ihn vermittle — in Deutschland § 43 IfSG, in Österreich die LMSVG-Schulung." },
  { icon: Clock, title: "Schnelle Rückmeldung", text: "Ich melde mich innerhalb von 24 Stunden — bei akutem Ausfall oft in unter einer Stunde." },
  { icon: MapPin, title: "Deutschland & Österreich", text: "Köche und Betriebe aus beiden Ländern — das Umkreis-Matching rechnet auch über die Grenze, etwa Salzburg und Oberbayern." },
  { icon: FileText, title: "Klare Verträge", text: "Beide Seiten wissen vorab genau, was gilt. Verträge gibt es blanko zum Nachlesen und digital zum Unterschreiben." },
];

export default function Vermittlung() {
  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="vermittlung-page">
      <p className="text-[10px] uppercase tracking-[0.35em] text-[#BA6D52]">Koch-Vermittlung · Betriebe & Privatkunden · DE & AT</p>
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter mt-6 max-w-3xl">
        Köche und Küchen,<br />die zusammenpassen.
      </h1>
      <p className="text-[#757067] font-light text-lg mt-6 max-w-2xl leading-relaxed">
        Ich stehe selbst am Herd und kenne beide Seiten: den Betrieb, dem morgen der Koch fehlt, und
        den Kollegen, der lieber kocht als Angebote schreibt. Genau dazwischen vermittle ich —
        für <b className="text-[#2C2A26] font-medium">Betriebe und Privatkunden</b>, in ganz Deutschland
        und Österreich, auf Wunsch auch über die Grenze.
      </p>

      <div className="grid md:grid-cols-3 gap-6 mt-14">
        {OPTIONS.map((o) => (
          <Link
            key={o.href}
            to={o.href}
            data-testid={o.testid}
            className="group relative bg-white border border-[#E3DECE] p-8 transition-colors duration-300 hover:border-[#BA6D52]"
          >
            <o.icon className="w-7 h-7 text-[#BA6D52]" strokeWidth={1.4} />
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#757067] mt-6">{o.eyebrow}</p>
            <h2 className="font-serif text-2xl font-light mt-3 tracking-tight">{o.title}</h2>
            <p className="text-sm text-[#575349] mt-4 leading-relaxed">{o.description}</p>
            <ul className="mt-6 space-y-2">
              {o.bullets.map((b) => (
                <li key={b} className="text-sm text-[#575349] flex gap-3 leading-relaxed">
                  <span className="text-[#BA6D52] mt-[2px]">—</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <span className="inline-flex items-center gap-2 mt-8 text-[11px] uppercase tracking-[0.2em] text-[#BA6D52]">
              {o.cta}
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </span>
          </Link>
        ))}
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mt-20 pt-14 border-t border-[#E3DECE]">
        {TRUST.map((t) => (
          <div key={t.title}>
            <t.icon className="w-5 h-5 text-[#BA6D52]" strokeWidth={1.5} />
            <h3 className="text-base md:text-lg font-serif mt-4">{t.title}</h3>
            <p className="text-sm text-[#757067] mt-2 leading-relaxed">{t.text}</p>
          </div>
        ))}
      </div>

      <div className="mt-16 bg-[#F2EFE9] p-8 md:p-10">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52]">Verträge zum Nachlesen</p>
        <h3 className="font-serif text-2xl font-light mt-3">Erst lesen, dann entscheiden.</h3>
        <p className="text-sm text-[#575349] mt-3 max-w-2xl leading-relaxed">
          Beide Verträge sind kurz gehalten und ohne Kleingedrucktes. Laden Sie sie in Ruhe herunter —
          unterschrieben wird später, entweder klassisch auf Papier oder direkt im Browser.
        </p>
        <div className="flex flex-wrap gap-3 mt-6">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/vermittlung/vertrag/mietkoch.pdf`}
            target="_blank"
            rel="noreferrer"
            data-testid="download-vertrag-koch"
            className="border border-[#BA6D52] text-[#BA6D52] hover:bg-white px-5 py-3 text-[11px] uppercase tracking-[0.2em] transition-colors"
          >
            ⬇ Vermittlungsvertrag (Koch)
          </a>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/vermittlung/vertrag/betrieb.pdf`}
            target="_blank"
            rel="noreferrer"
            data-testid="download-vertrag-betrieb"
            className="border border-[#BA6D52] text-[#BA6D52] hover:bg-white px-5 py-3 text-[11px] uppercase tracking-[0.2em] transition-colors"
          >
            ⬇ Provisionsvereinbarung (Betrieb)
          </a>
        </div>
      </div>
    </div>
  );
}
